import React, { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import {
    CheckCircle2,
    XCircle,
    ChevronRight,
    Smartphone,
    CreditCard,
    Headphones,
    Award,
    TrendingUp,
    ShieldCheck,
    Loader2,
    AlertCircle,
} from "lucide-react";
import { getBookmakerBySlug, BOOKMAKERS } from "../lib/data";
import { BrandBadge } from "../components/BrandBadge";
import { StarRating } from "../components/StarRating";
import { CircularScoreGauge } from "../components/CircularScoreGauge";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { SEO, reviewSchema, breadcrumbSchema } from "../components/SEO";

const Section = ({ id, title, children }) => (
    <section id={id} className="scroll-mt-24" data-testid={`section-${id}`}>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-5 tracking-tight">
            {title}
        </h2>
        <div className="text-gray-300 leading-relaxed space-y-4">
            {children}
        </div>
    </section>
);

const COMMUNITY_REVIEWS = {
    unibet: [
        { id: 1, user: "Thomas L.", date: "15 Mai 2026", rating: 5, text: "L'application mobile est vraiment au-dessus du lot. Très fluide pour parier en direct, et les retraits instantanés fonctionnent nickel." },
        { id: 2, user: "Alexandre G.", date: "02 Mai 2026", rating: 4, text: "Le bonus est sympa mais versé en freebets, pas en cash. Sinon l'offre de paris sur la Ligue 1 est juste énorme." },
    ],
    betclic: [
        { id: 1, user: "Mathieu D.", date: "24 Mai 2026", rating: 5, text: "Le remboursement en cash réel est le meilleur bonus du marché. Pari perdu, argent retiré le lendemain sans problème !" },
        { id: 2, user: "Nicolas B.", date: "18 Mai 2026", rating: 4, text: "Super appli, très ergonomique et facile pour débuter. Les cotes sont un poil plus basses que Winamax mais l'expérience utilisateur compense largement." },
    ],
    winamax: [
        { id: 1, user: "Florent R.", date: "28 Mai 2026", rating: 5, text: "Les meilleures cotes de France sans discussion. Le site de poker est aussi top. Service de retrait instantané hyper efficace." },
        { id: 2, user: "Julien M.", date: "10 Mai 2026", rating: 4, text: "Dommage qu'il n'y ait pas de chat en direct pour le service client. Mais pour les cotes pures, c'est le numéro 1 incontestable." },
    ],
    bet365: [
        { id: 1, user: "Sébastien P.", date: "22 Mai 2026", rating: 5, text: "Le créateur de paris (Bet Builder) est incroyable, on peut combiner presque tout sur un seul match. Vraiment content de leur arrivée en France." },
        { id: 2, user: "Pierre V.", date: "14 Mai 2026", rating: 4, text: "Des tonnes de matchs en streaming. Interface un peu technique au début, mais tellement complète au niveau des stats." },
    ],
    olybet: [
        { id: 1, user: "Marc A.", date: "25 Mai 2026", rating: 4, text: "Le programme de fidélité est super intéressant avec des places de match à gagner. Les cotes sur la NBA sont très correctes." },
        { id: 2, user: "Jérôme K.", date: "08 Mai 2026", rating: 3, text: "Le bonus par paliers est un peu complexe à débloquer en totalité. Mais le site est sérieux et le service client est à l'écoute." },
    ],
    betsson: [
        { id: 1, user: "Damien F.", date: "29 Mai 2026", rating: 5, text: "Les 10 € offerts sans dépôt m'ont permis de tester la plateforme gratuitement. Le concept BetSwipe est fun pour parier rapidement." },
        { id: 2, user: "Lucas T.", date: "12 Mai 2026", rating: 4, text: "Très bonnes cotes sur le football. L'interface mobile est un peu originale mais on s'y fait vite. Retrait validé en 24h." },
    ],
    default: [
        { id: 1, user: "Parieur anonyme", date: "20 Mai 2026", rating: 4, text: "Plateforme fiable et conforme aux régulations ANJ. Bon choix d'options." },
        { id: 2, user: "Antoine B.", date: "05 Mai 2026", rating: 4, text: "Bon site dans l'ensemble. Interface correcte et transactions sécurisées." }
    ]
};

const CommunityReviews = ({ bookmakerSlug }) => {
    const staticReviews = COMMUNITY_REVIEWS[bookmakerSlug] || COMMUNITY_REVIEWS.default;
    const [dbReviews, setDbReviews] = useState([]);
    const [loading, setLoading] = useState(true);

    const [userRating, setUserRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [username, setUsername] = useState("");
    const [comment, setComment] = useState("");
    
    const [submitting, setSubmitting] = useState(false);
    const [voted, setVoted] = useState(false);
    const [error, setError] = useState("");

    const API = process.env.REACT_APP_BACKEND_URL || "";

    useEffect(() => {
        let active = true;
        setLoading(true);
        axios
            .get(`${API}/api/bookmakers/${bookmakerSlug}/reviews`)
            .then((res) => {
                if (active) {
                    setDbReviews(res.data || []);
                    setLoading(false);
                }
            })
            .catch((err) => {
                console.error("Failed to load reviews:", err);
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [bookmakerSlug, API]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (userRating === 0) {
            setError("Veuillez sélectionner une note de 1 à 5 étoiles.");
            return;
        }
        if (!username.trim() || !comment.trim()) {
            setError("Veuillez remplir tous les champs requis.");
            return;
        }

        setSubmitting(true);
        setError("");

        try {
            const { data } = await axios.post(`${API}/api/bookmakers/${bookmakerSlug}/reviews`, {
                username: username.trim(),
                rating: userRating,
                comment: comment.trim(),
            });
            
            if (data?.success && data?.review) {
                setDbReviews((prev) => [data.review, ...prev]);
                setVoted(true);
                setUsername("");
                setComment("");
            } else {
                setError("Une erreur est survenue lors de l'enregistrement de votre avis.");
            }
        } catch (err) {
            console.error("Review submission error:", err);
            const detail = err?.response?.data?.detail;
            setError(typeof detail === "string" ? detail : "Impossible de soumettre l'avis. Veuillez réessayer.");
        } finally {
            setSubmitting(false);
        }
    };

    const allReviews = [...dbReviews, ...staticReviews];

    return (
        <div className="space-y-6" data-testid="community-reviews">
            {/* Summary statistics */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-6 items-center bg-[#162236] p-5 rounded-xl border border-[#2A4A6B]">
                <div className="text-center md:border-r md:border-[#2A4A6B] md:pr-6">
                    <div className="text-5xl font-heading font-black text-white">4.5</div>
                    <div className="flex justify-center my-2">
                        <StarRating value={4} size={16} />
                    </div>
                    <div className="text-xs text-gray-400">
                        Basé sur {148 + dbReviews.length} avis
                    </div>
                </div>
                <div className="space-y-2">
                    {[
                        { stars: 5, pct: 68 },
                        { stars: 4, pct: 22 },
                        { stars: 3, pct: 6 },
                        { stars: 2, pct: 3 },
                        { stars: 1, pct: 1 }
                    ].map((row) => (
                        <div key={row.stars} className="flex items-center gap-3 text-xs">
                            <span className="w-3 text-gray-400 font-bold">{row.stars}</span>
                            <span className="text-[#FFB300]">★</span>
                            <div className="flex-1 h-2 bg-[#0D1B2A] rounded-full overflow-hidden">
                                <div className="h-full bg-[#00C853] rounded-full" style={{ width: `${row.pct}%` }} />
                            </div>
                            <span className="w-8 text-right text-gray-400 font-medium">{row.pct}%</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* List of user reviews */}
            <div className="space-y-4">
                {loading && dbReviews.length === 0 ? (
                    <div className="flex items-center justify-center py-6 text-gray-400 text-xs gap-2">
                        <Loader2 size={14} className="animate-spin text-[#00C853]" />
                        Chargement des avis récents...
                    </div>
                ) : null}

                {allReviews.map((r, index) => {
                    const name = r.username || r.user;
                    const dateText = r.date;
                    const commentText = r.comment || r.text;
                    const ratingValue = r.rating;
                    const isDbReview = !!r.username;

                    return (
                        <div 
                            key={r.id || `review-${index}`} 
                            className={`card-elev p-5 transition-all ${
                                isDbReview 
                                    ? "bg-[#0D1B2A]/70 border-l-4 border-l-[#00C853]" 
                                    : "bg-[#0D1B2A]/40"
                            }`}
                        >
                            <div className="flex justify-between items-start gap-4 mb-2 flex-wrap">
                                <div>
                                    <span className="font-heading font-bold text-white text-sm block flex items-center gap-2">
                                        {name}
                                        {isDbReview && (
                                            <span className="text-[10px] bg-[#00C853]/10 border border-[#00C853]/35 text-[#00C853] px-1.5 py-0.5 rounded font-sans uppercase font-bold tracking-wider">
                                                Visiteur
                                            </span>
                                        )}
                                    </span>
                                    <span className="text-[10px] text-gray-500">{dateText}</span>
                                </div>
                                <StarRating value={ratingValue} size={12} />
                            </div>
                            <p className="text-gray-300 text-sm leading-relaxed">{commentText}</p>
                        </div>
                    );
                })}
            </div>

            {/* Interactive review submission */}
            <div className="card-elev p-5 border-dashed border-[#2A4A6B] bg-transparent">
                <h4 className="font-heading font-bold text-white text-sm mb-3 uppercase tracking-wider">
                    Votre avis nous intéresse
                </h4>
                {voted ? (
                    <div className="text-sm text-[#00C853] font-semibold bg-[#00C853]/10 border border-[#00C853]/30 p-4 rounded-lg flex items-start gap-3 animate-fade-in">
                        <CheckCircle2 size={18} className="flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="font-heading font-bold">Avis soumis avec succès !</p>
                            <p className="text-xs text-gray-400 font-normal mt-1 leading-relaxed">
                                Merci d'avoir partagé votre expérience avec la communauté. Votre note et votre commentaire sont maintenant en ligne.
                            </p>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs text-gray-400 mb-1.5 block uppercase tracking-wider font-semibold">
                                    Nom ou Pseudo
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    placeholder="Ex: Jean D."
                                    disabled={submitting}
                                    className="w-full bg-[#0D1B2A] border border-[#2A4A6B] rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00C853] focus:border-transparent disabled:opacity-60"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-gray-400 mb-1.5 block text-left uppercase tracking-wider font-semibold">
                                    Note d'évaluation
                                </label>
                                <div className="flex items-center gap-2 h-9 justify-start">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            key={star}
                                            type="button"
                                            onClick={() => setUserRating(star)}
                                            onMouseEnter={() => setHoverRating(star)}
                                            onMouseLeave={() => setHoverRating(0)}
                                            disabled={submitting}
                                            className="text-2xl transition-transform duration-100 hover:scale-125 focus:outline-none"
                                            aria-label={`Attribuer ${star} étoiles`}
                                        >
                                            <span
                                                className={
                                                    star <= (hoverRating || userRating)
                                                        ? "text-[#FFB300]"
                                                        : "text-gray-600"
                                                }
                                            >
                                                ★
                                            </span>
                                        </button>
                                    ))}
                                    {userRating > 0 && (
                                        <span className="text-xs text-gray-400 ml-2">
                                            ({userRating} sur 5)
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="text-xs text-gray-400 mb-1.5 block uppercase tracking-wider font-semibold">
                                Votre commentaire
                            </label>
                            <textarea
                                required
                                rows={3}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Partagez votre avis sur l'application, les bonus, le choix de paris, la rapidité des retraits..."
                                disabled={submitting}
                                className="w-full bg-[#0D1B2A] border border-[#2A4A6B] rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00C853] focus:border-transparent disabled:opacity-60 resize-none"
                            />
                        </div>

                        {error && (
                            <div className="flex items-center gap-2 text-xs text-[#FFB300] bg-[#FFB300]/10 border border-[#FFB300]/30 p-2.5 rounded-lg">
                                <AlertCircle size={14} className="flex-shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            className="btn-green text-xs !py-2 !px-4 flex items-center gap-2 disabled:opacity-75"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 size={14} className="animate-spin text-black" />
                                    <span>Envoi en cours...</span>
                                </>
                            ) : (
                                <span>Soumettre l'avis</span>
                            )}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

const Review = () => {
    const { slug } = useParams();
    const b = getBookmakerBySlug(slug);

    if (!b) return <Navigate to="/comparatif" replace />;

    const others = BOOKMAKERS.filter((x) => x.slug !== slug).slice(0, 3);

    return (
        <div data-testid={`review-page-${slug}`}>
            <SEO
                title={`Avis ${b.name} 2026 — Note ${b.score}/10 | Pronospot.com`}
                description={`Avis détaillé sur ${b.name} : ${b.bonus}, cotes, marchés foot, application mobile. ${b.description.slice(0, 80)}...`}
                jsonLd={[
                    reviewSchema(b),
                    breadcrumbSchema([
                        { name: "Accueil", url: "https://pronospot.com/" },
                        { name: "Avis", url: "https://pronospot.com/comparatif" },
                        {
                            name: b.name,
                            url: `https://pronospot.com/avis/${b.slug}`,
                        },
                    ]),
                ]}
            />
            <section className="container-x pt-10">
                <Breadcrumbs
                    items={[
                        { label: "Avis", to: "/comparatif" },
                        { label: b.name },
                    ]}
                />
            </section>

            {/* HERO */}
            <section className="container-x pb-10" data-testid="review-hero">
                <div className="card-elev p-6 sm:p-10 grass-texture">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
                        <div className="lg:col-span-2 space-y-5">
                            <div className="flex items-center gap-4 flex-wrap">
                                <BrandBadge
                                    name={b.name}
                                    color={b.color}
                                    size="lg"
                                />
                                {b.badge && (
                                    <span className="badge-amber">{b.badge}</span>
                                )}
                            </div>
                            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tighter">
                                Avis {b.name} 2026
                            </h1>
                            <p className="text-gray-300 text-base sm:text-lg max-w-2xl">
                                {b.description}
                            </p>
                            <div className="flex items-center gap-3 flex-wrap pt-2">
                                <div className="flex items-center gap-2 text-xs text-gray-400">
                                    <ShieldCheck
                                        size={14}
                                        className="text-[#00C853]"
                                    />
                                    {b.license}
                                </div>
                                <StarRating value={b.stars} size={18} />
                            </div>
                        </div>

                        <div className="flex flex-col items-center justify-center bg-[#0D1B2A] rounded-xl p-6 border border-[#2A4A6B]">
                            <span className="text-xs text-gray-400 uppercase tracking-wider mb-3">
                                Note globale
                            </span>
                            <CircularScoreGauge score={b.score} size={160} />
                            <a
                                href={b.affiliateUrl}
                                target="_blank"
                                rel="sponsored nofollow noopener"
                                className="btn-green w-full mt-5"
                                data-testid={`hero-cta-${b.slug}`}
                            >
                                Accéder au site
                                <ChevronRight size={18} />
                            </a>
                        </div>
                    </div>

                    {/* Quick Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-8 border-t border-[#2A4A6B]">
                        {[
                            {
                                label: "Bonus",
                                value: b.bonusShort,
                                color: "#FFB300",
                            },
                            {
                                label: "Cotes",
                                value: b.oddsAvg,
                                color: "#00C853",
                            },
                            {
                                label: "Marchés",
                                value: `${b.markets}+`,
                                color: "#00C853",
                            },
                            {
                                label: "Support",
                                value: b.liveStreaming ? "Live ✓" : "Standard",
                                color: "#00C853",
                            },
                        ].map((s) => (
                            <div key={s.label}>
                                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">
                                    {s.label}
                                </div>
                                <div
                                    className="font-heading font-bold text-lg"
                                    style={{ color: s.color }}
                                >
                                    {s.value}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* MAIN CONTENT + STICKY SIDEBAR */}
            <section className="container-x pb-20">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10">
                    <article className="space-y-14">
                        {/* PROS & CONS */}
                        <div
                            className="grid grid-cols-1 md:grid-cols-2 gap-6"
                            data-testid="pros-cons-section"
                        >
                            <div className="card-elev p-6">
                                <h3 className="font-heading font-bold text-[#00C853] uppercase tracking-wider text-sm mb-4 flex items-center gap-2">
                                    <CheckCircle2 size={18} />
                                    Points forts
                                </h3>
                                <ul className="space-y-3">
                                    {b.pros.map((p) => (
                                        <li
                                            key={p}
                                            className="flex gap-2 text-sm text-gray-300"
                                        >
                                            <CheckCircle2
                                                size={16}
                                                className="text-[#00C853] flex-shrink-0 mt-0.5"
                                            />
                                            {p}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="card-elev p-6">
                                <h3 className="font-heading font-bold text-[#FFB300] uppercase tracking-wider text-sm mb-4 flex items-center gap-2">
                                    <XCircle size={18} />
                                    Points faibles
                                </h3>
                                <ul className="space-y-3">
                                    {b.cons.map((c) => (
                                        <li
                                            key={c}
                                            className="flex gap-2 text-sm text-gray-300"
                                        >
                                            <XCircle
                                                size={16}
                                                className="text-[#FFB300] flex-shrink-0 mt-0.5"
                                            />
                                            {c}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        <Section id="bonus" title="Le Bonus en Détail">
                            <p>
                                {b.name} propose actuellement{" "}
                                <span className="text-[#FFB300] font-semibold">
                                    {b.bonus}
                                </span>{" "}
                                pour tout nouvel inscrit majeur en France
                                métropolitaine. Voici comment réclamer votre
                                bonus pas à pas :
                            </p>
                            <ol className="space-y-3 mt-4">
                                {b.bonusSteps.map((s, i) => (
                                    <li
                                        key={i}
                                        className="flex gap-3 items-start"
                                    >
                                        <span className="w-7 h-7 rounded-full bg-[#00C853] text-black font-bold text-sm flex items-center justify-center flex-shrink-0">
                                            {i + 1}
                                        </span>
                                        <span className="text-gray-300 pt-0.5">
                                            {s}
                                        </span>
                                    </li>
                                ))}
                            </ol>
                        </Section>

                        <Section id="marches" title="Les Marchés Football">
                            <p>
                                Avec plus de{" "}
                                <strong className="text-white">
                                    {b.markets} marchés
                                </strong>{" "}
                                disponibles par match top niveau, {b.name}{" "}
                                couvre largement la Ligue 1, la Champions
                                League, la Coupe de France et les principaux
                                championnats européens. Vous trouverez tous
                                les marchés standards (1N2, double chance,
                                handicap, over/under) ainsi que les marchés
                                spéciaux : buteurs, corners, cartons, score
                                exact, mi-temps.
                            </p>
                        </Section>

                        <Section id="cotes" title="Cotes & Valeur">
                            <p>
                                Le payout moyen de {b.name} est de{" "}
                                <span className="text-[#00C853] font-semibold">
                                    {b.oddsAvg}
                                </span>
                                , ce qui le place parmi les opérateurs les
                                plus compétitifs du marché français. Nos
                                analyses portent sur 200+ matchs de Ligue 1
                                sur les 3 derniers mois.
                            </p>
                        </Section>

                        <Section id="app" title="Application Mobile">
                            <div className="flex items-start gap-3">
                                <Smartphone className="text-[#00C853]" />
                                <p>
                                    L'application {b.name} est disponible sur
                                    iOS et Android. Interface fluide,
                                    notifications personnalisables, dépôts
                                    et retraits intégrés. Note moyenne sur
                                    les stores : 4.5/5.
                                </p>
                            </div>
                        </Section>

                        <Section id="paiements" title="Paiements & Retraits">
                            <div className="flex items-start gap-3 mb-4">
                                <CreditCard className="text-[#00C853]" />
                                <p>
                                    Méthodes acceptées :{" "}
                                    {b.payments.join(", ")}. Dépôt minimum
                                    5€, retraits sous 24 à 72h selon la
                                    méthode.
                                </p>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-[#1E3A5F]">
                                        <tr>
                                            <th className="px-4 py-3 text-left font-semibold text-white text-xs uppercase">
                                                Méthode
                                            </th>
                                            <th className="px-4 py-3 text-left font-semibold text-white text-xs uppercase">
                                                Dépôt min.
                                            </th>
                                            <th className="px-4 py-3 text-left font-semibold text-white text-xs uppercase">
                                                Délai retrait
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {b.payments.map((p, i) => (
                                            <tr
                                                key={p}
                                                className={
                                                    i % 2
                                                        ? "bg-[#162236]"
                                                        : "bg-[#0D1B2A]"
                                                }
                                            >
                                                <td className="px-4 py-3 text-gray-300">
                                                    {p}
                                                </td>
                                                <td className="px-4 py-3 text-gray-300">
                                                    5€
                                                </td>
                                                <td className="px-4 py-3 text-gray-300">
                                                    24-72h
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Section>

                        <Section id="support" title="Service Client">
                            <div className="flex items-start gap-3">
                                <Headphones className="text-[#00C853]" />
                                <p>
                                    Support disponible :{" "}
                                    <strong className="text-white">
                                        {b.support}
                                    </strong>
                                    . Langue : français. Délai de réponse
                                    moyen : moins de 15 minutes par chat.
                                </p>
                            </div>
                        </Section>

                        <Section id="avis-commu" title="Avis de la Communauté">
                            <CommunityReviews bookmakerSlug={b.slug} />
                        </Section>

                        <Section id="verdict" title="Notre Verdict">
                            <div className="card-elev p-6 border-l-4 border-l-[#00C853]">
                                <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
                                    <div className="flex items-center gap-3">
                                        <Award
                                            className="text-[#00C853]"
                                            size={28}
                                        />
                                        <h3 className="font-heading font-bold text-white text-xl">
                                            Verdict Pronospot
                                        </h3>
                                    </div>
                                    <div className="font-heading text-4xl font-extrabold text-[#00C853]">
                                        {b.score.toFixed(1)}
                                        <span className="text-base text-gray-400 font-normal">
                                            {" "}
                                            /10
                                        </span>
                                    </div>
                                </div>
                                <p>{b.verdict}</p>
                                <a
                                    href={b.affiliateUrl}
                                    target="_blank"
                                    rel="sponsored nofollow noopener"
                                    className="btn-green mt-5"
                                    data-testid={`verdict-cta-${b.slug}`}
                                >
                                    Accéder au site
                                    <ChevronRight size={18} />
                                </a>
                            </div>
                        </Section>
                    </article>

                    {/* STICKY SIDEBAR */}
                    <aside className="hidden lg:block">
                        <div className="sticky top-24 space-y-5">
                            <div
                                className="card-elev p-6"
                                data-testid="sticky-cta-card"
                            >
                                <BrandBadge
                                    name={b.name}
                                    color={b.color}
                                    size="md"
                                />
                                <div className="mt-4 mb-2 text-xs text-gray-400 uppercase tracking-wider">
                                    Bonus de bienvenue
                                </div>
                                <div className="font-heading text-xl font-bold text-[#FFB300] leading-tight">
                                    {b.bonus}
                                </div>
                                <div className="mt-5 flex items-center gap-2">
                                    <CircularScoreGauge
                                        score={b.score}
                                        size={80}
                                    />
                                    <div>
                                        <StarRating
                                            value={b.stars}
                                            size={14}
                                        />
                                        <div className="text-xs text-gray-400 mt-1">
                                            Note Pronospot
                                        </div>
                                    </div>
                                </div>
                                <a
                                    href={b.affiliateUrl}
                                    target="_blank"
                                    rel="sponsored nofollow noopener"
                                    className="btn-green w-full mt-5"
                                    data-testid={`sidebar-cta-${b.slug}`}
                                >
                                    Accéder au site
                                    <ChevronRight size={16} />
                                </a>
                                <p className="text-[10px] text-gray-500 mt-3 leading-relaxed">
                                    18+ | Jouer comporte des risques.
                                    Conditions sur le site de l'opérateur.
                                </p>
                            </div>

                            <div className="card-elev p-5">
                                <h4 className="font-heading font-bold text-white text-sm mb-3 uppercase tracking-wider">
                                    Sommaire
                                </h4>
                                <ul className="space-y-2 text-sm">
                                    {[
                                        ["bonus", "Le Bonus"],
                                        ["marches", "Marchés foot"],
                                        ["cotes", "Cotes & Valeur"],
                                        ["app", "App mobile"],
                                        ["paiements", "Paiements"],
                                        ["support", "Service client"],
                                        ["avis-commu", "Avis joueurs"],
                                        ["verdict", "Verdict"],
                                    ].map(([id, label]) => (
                                        <li key={id}>
                                            <a
                                                href={`#${id}`}
                                                className="text-gray-400 hover:text-[#00C853] flex items-center gap-1.5"
                                            >
                                                <TrendingUp size={12} />
                                                {label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </aside>
                </div>

                {/* Other bookmakers */}
                <div className="mt-20" data-testid="other-bookmakers">
                    <h3 className="font-heading text-2xl font-bold text-white mb-6">
                        Comparer avec d'autres bookmakers
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {others.map((o) => (
                            <Link
                                key={o.slug}
                                to={`/avis/${o.slug}`}
                                className="card-elev p-5 flex items-center justify-between"
                            >
                                <div>
                                    <BrandBadge
                                        name={o.name}
                                        color={o.color}
                                        size="sm"
                                    />
                                    <div className="mt-3 text-[#FFB300] font-semibold text-sm">
                                        {o.bonusShort}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="font-heading text-2xl font-bold text-white">
                                        {o.score.toFixed(1)}
                                    </div>
                                    <ChevronRight
                                        size={16}
                                        className="text-[#00C853] ml-auto mt-1"
                                    />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Review;
